import { LoaderFunctionArgs, useParams } from 'react-router-dom';

import {
  Badge,
  Button,
  Center,
  Flex,
  Grid,
  GridItem,
  Spinner,
  Stack,
  Switch,
  Text,
  useBoolean,
  useDisclosure,
} from '@chakra-ui/react';

import { QueryClient, useQueryClient } from '@tanstack/react-query';

import { format, parseISO } from 'date-fns';

import { formatValue } from 'react-currency-input-field';

import {
  CalendarOutline,
  DEFAULT_DATE_FORMAT,
  Individual,
  PayCalendarSalaryAllowance,
  EyeOutline as Eye,
  EyeSlashOutline as EyeSlash,
  InviteOutline as Invite,
} from '@aslanpay/portal-common-lib';

import PreviousEmploymentSection from 'app/components/ManagePeople/PreviousEmploymentSection';
import EditEmployeeTerminationDateModal from 'app/components/ManagePeople/ManageDefaultEmployees/EditEmployeeTerminationDate/EditEmployeeTerminationDateModal';
import ManageUnpaidLeaveModal from 'app/components/ManagePeople/ManageDefaultEmployees/ManageUnpaidLeaveModal';
import TerminateEmployeeModal from 'app/components/ManagePeople/ManageDefaultEmployees/TerminateEmployee/TerminateEmployeeModal';
import InviteIndividualModal from 'app/components/Individuals/InviteIndividualModal';
import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';

import { Status } from 'app/constants/individual';

import EditSalaryAllowanceModal from 'app/components/ManagePeople/ManageDefaultEmployees/EditSalaryAllowanceModal';
import useFetchCompanyConfiguration from 'app/hooks/useFetchCompanyConfiguration';
import useFetchIndividual, { fetchIndividualQuery } from 'app/hooks/useFetchIndividual';
import useFetchIndividualConfiguration from 'app/hooks/useFetchIndividualConfiguration';
import useFetchPayCalendar from 'app/hooks/useFetchPayCalendar';

// Extended Individual type that includes allowance fields
interface IndividualWithAllowance extends Individual {
  globalAllowancePercentage?: PayCalendarSalaryAllowance;
}

export const employeeProfileLoader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const query = fetchIndividualQuery(params.employeeId);
    return queryClient.getQueryData(query.queryKey) ?? (await queryClient.fetchQuery(query));
  };

function DefaultEmployeeProfile() {
  const queryClient = useQueryClient();

  const { isOpen: isInviteOpen, onOpen: onOpenInvite, onClose: onCloseInvite } = useDisclosure();

  const {
    isOpen: isTerminateOpen,
    onOpen: onOpenTerminate,
    onClose: onCloseTerminate,
  } = useDisclosure();

  const {
    isOpen: isEditTerminationDateOpen,
    onOpen: onOpenEditTerminationDate,
    onClose: onCloseEditTerminationDate,
  } = useDisclosure();

  const {
    isOpen: isManageUnpaidLeaveOpen,
    onOpen: onOpenManageUnpaidLeave,
    onClose: onCloseManageUnpaidLeave,
  } = useDisclosure();

  const {
    isOpen: isEditSalaryAllowanceOpen,
    onOpen: onOpenEditSalaryAllowance,
    onClose: onCloseEditSalaryAllowance,
  } = useDisclosure();

  const [isSalaryVisible, setIsSalaryVisible] = useBoolean();

  const { employeeId } = useParams();

  const { data: employee, isInitialLoading: isLoadingEmployee } = useFetchIndividual(
    employeeId
  ) as {
    data: IndividualWithAllowance | undefined;
    isInitialLoading: boolean;
  };

  const { data: payCalendar, isLoading: isLoadingPayCalendar } = useFetchPayCalendar(
    employee?.payCalendarId
  );

  const { data: companyConfig } = useFetchCompanyConfiguration(employee?.companyId || '');
  const { data: individualConfig } = useFetchIndividualConfiguration(employee?.id);

  const isSalaried = employee?.salaried === true;
  const showAnnualGrossSalary =
    isSalaried &&
    (companyConfig?.generalCompanyConfiguration?.showEmployeeAnnualGrossSalary ?? false);

  // Check both company-level and individual-level configuration
  const isOnDemandPayEnabled =
    companyConfig?.modulesConfig?.modules?.ON_DEMAND_PAY === 'ENABLED' &&
    individualConfig?.modulesConfig?.modules?.ON_DEMAND_PAY === 'ENABLED';

  const isReserveEnabled =
    companyConfig?.modulesConfig?.modules?.RESERVE === 'ENABLED' &&
    individualConfig?.modulesConfig?.modules?.RESERVE === 'ENABLED';

  // Show Aslan details section if:
  // 1. Modules are enabled (original logic), OR
  // 2. There's actual allowance data available (fallback for when individual config fails)
  const hasAllowanceData =
    employee?.globalAllowancePercentage !== undefined ||
    employee?.odpAllowancePercentage !== undefined ||
    payCalendar?.globalAllowancePercentage !== undefined ||
    payCalendar?.odpAllowancePercentage !== undefined;

  const shouldShowAllowanceSection = isOnDemandPayEnabled || isReserveEnabled || hasAllowanceData;

  const handleInvalidateQuery = () => {
    queryClient.invalidateQueries({ queryKey: ['fetchIndividual', employeeId] });
    queryClient.refetchQueries({ queryKey: ['fetchIndividual', employeeId] });
  };

  const renderModals = () => (
    <>
      <InviteIndividualModal
        selectedIndividuals={[employee?.id || '']}
        isOpen={isInviteOpen}
        onClose={onCloseInvite}
        onInvite={handleInvalidateQuery}
      />
      <TerminateEmployeeModal
        id={employee?.id || ''}
        isOpen={isTerminateOpen}
        onClose={onCloseTerminate}
        onTerminate={handleInvalidateQuery}
      />
      <EditEmployeeTerminationDateModal
        id={employee?.id || ''}
        isOpen={isEditTerminationDateOpen}
        onClose={onCloseEditTerminationDate}
        onEditTerminate={handleInvalidateQuery}
      />
      <ManageUnpaidLeaveModal
        id={employee?.id || ''}
        isOpen={isManageUnpaidLeaveOpen}
        onClose={onCloseManageUnpaidLeave}
        onUnpaidLeave={handleInvalidateQuery}
      />
      <EditSalaryAllowanceModal
        id={employee?.id || ''}
        isOpen={isEditSalaryAllowanceOpen}
        onClose={onCloseEditSalaryAllowance}
        onEdit={handleInvalidateQuery}
      />
    </>
  );

  const renderSalaryVisibilityButton = () => {
    const Icon = isSalaryVisible ? EyeSlash : Eye;
    const text = isSalaryVisible ? 'Hide salary' : 'Show salary';

    return (
      <Button
        variant="link"
        size="sm"
        h="auto"
        p={0}
        leftIcon={<Icon boxSize="14px" />}
        onClick={() => setIsSalaryVisible.toggle()}
      >
        {text}
      </Button>
    );
  };

  const username = employee?.firstName
    ? `${employee?.firstName} ${employee?.lastName}`
    : employee?.email;

  const isPayCalendarDefaultSalary =
    payCalendar?.defaultAnnualGrossSalary && !employee?.annualGrossSalary;

  return (
    <AuthenticatedPageWrapper title={username || 'Profile'}>
      {isLoadingEmployee || isLoadingPayCalendar ? (
        <Center width="full" height="full" flexGrow={1}>
          <Spinner color="brand.purple" size="xl" mb="50px" />
        </Center>
      ) : (
        <Flex flexDir="column" gap="30px" flexGrow={1}>
          {renderModals()}
          <Flex gap="20px" align="flex-start" justify="space-between">
            <Stack spacing="10px">
              <Text as="h2" textStyle="h2" color="brand.darkPurple" wordBreak="break-word">
                {username}
              </Text>
              <Text
                color="brand.black"
                textStyle="smallRegular"
              >{`Aslan ID: ${employee?.friendlyId}`}</Text>
            </Stack>

            <Button
              flexShrink={0}
              width="auto"
              size="sm"
              leftIcon={<Invite boxSize="20px" />}
              onClick={onOpenInvite}
              isDisabled={employee?.status !== 'CREATED' && employee?.status !== 'INVITED'}
            >
              {employee?.status === 'INVITED' ? 'Invite again' : 'Invite'}
            </Button>
          </Flex>

          <Flex flexDir="column" gap="40px">
            <Flex
              flexDir="column"
              borderRadius="10px"
              border="1px solid"
              borderColor="brand.lightGray1"
              mx={{ base: 0, md: '60px', xl: '100px' }}
              flexWrap="wrap"
            >
              <Flex w="full" justifyContent="start" p="20px">
                <Text color="brand.purple" textStyle="h5Bold">
                  Profile
                </Text>

                {employee?.status && (
                  <Badge colorScheme={`badge.${Status[employee?.status].color}`} ml="auto">
                    {Status[employee?.status].value}
                  </Badge>
                )}
              </Flex>
              <Flex
                w="full"
                flexDir={{ base: 'column', md: 'row' }}
                justifyContent="start"
                p="20px"
                borderTop="1px solid"
                borderColor="brand.lightGray1"
                gap="20px"
              >
                <Text as="h6" textStyle="h6SemiBold" color="brand.black" maxW="200px" w="full">
                  General details
                </Text>

                <Grid
                  templateColumns="1fr 1fr"
                  rowGap="15px"
                  columnGap="20px"
                  wordBreak="break-word"
                  w="full"
                >
                  <GridItem>
                    <Flex gap="6px" flexDir="column">
                      <Text color="brand.darkGray" textStyle="extraSmallRegular" flexShrink={0}>
                        First name
                      </Text>
                      <Text py="15px" textStyle="smallMedium">
                        {employee?.firstName || '—'}
                      </Text>
                    </Flex>
                  </GridItem>
                  <GridItem>
                    <Flex gap="6px" flexDir="column">
                      <Text color="brand.darkGray" textStyle="extraSmallRegular">
                        Last name
                      </Text>
                      <Text py="15px" textStyle="smallMedium">
                        {employee?.lastName || '—'}
                      </Text>
                    </Flex>
                  </GridItem>
                  <GridItem>
                    <Flex gap="6px" flexDir="column">
                      <Text color="brand.darkGray" textStyle="extraSmallRegular">
                        Job title
                      </Text>
                      <Text py="15px" textStyle="smallMedium">
                        {employee?.jobTitle || '—'}
                      </Text>
                    </Flex>
                  </GridItem>
                  {showAnnualGrossSalary && (
                    <GridItem>
                      <Flex gap="6px" flexDir="column">
                        <Text color="brand.darkGray" textStyle="extraSmallRegular">
                          Annual gross salary
                        </Text>
                        <Flex py="15px" align="center" gap="10px">
                          <Text textStyle="smallMedium" minW="100px">
                            {isSalaryVisible
                              ? formatValue({
                                  value:
                                    employee?.annualGrossSalary?.toString() ||
                                    payCalendar?.defaultAnnualGrossSalary?.toString(),
                                  prefix: '\u00A3',
                                  groupSeparator: ',',
                                  decimalSeparator: '.',
                                  decimalScale: 2,
                                }) || '—'
                              : '****'}
                          </Text>
                          {renderSalaryVisibilityButton()}
                        </Flex>
                        {isSalaryVisible && isPayCalendarDefaultSalary && (
                          <Text textStyle="extraExtraSmallMedium">(Pay calendar default)</Text>
                        )}
                      </Flex>
                    </GridItem>
                  )}
                  <GridItem>
                    <Flex gap="6px" flexDir="column">
                      <Text color="brand.darkGray" textStyle="extraSmallRegular">
                        Company email
                      </Text>
                      <Text py="15px" textStyle="smallMedium">
                        {employee?.email}
                      </Text>
                    </Flex>
                  </GridItem>
                </Grid>
              </Flex>
              <Flex
                w="full"
                flexDir={{ base: 'column', md: 'row' }}
                justifyContent="start"
                alignItems={{ base: 'start', md: 'center' }}
                p="20px"
                borderTop="1px solid"
                borderColor="brand.lightGray1"
                gap="20px"
              >
                <Text as="h6" textStyle="h6SemiBold" color="brand.black" maxW="200px" w="full">
                  Pay calendar
                </Text>

                <Text color="brand.black" textStyle="smallRegular">
                  {payCalendar?.name}
                </Text>
              </Flex>
              <Flex
                w="full"
                flexDir={{ base: 'column', md: 'row' }}
                justifyContent="start"
                alignItems={{ base: 'start', md: 'center' }}
                p="20px"
                borderTop="1px solid"
                borderColor="brand.lightGray1"
                gap="20px"
              >
                <Text as="h6" textStyle="h6SemiBold" color="brand.black" maxW="200px" w="full">
                  Accrual status
                </Text>

                <Flex w="full" alignItems="center">
                  <Text color="brand.black" textStyle="smallRegular">
                    <Badge
                      colorScheme={`badge.${
                        employee?.status === 'RESTRICTED' || employee?.onUnpaidLeave
                          ? 'unpaid'
                          : 'active'
                      }`}
                    >
                      {employee?.status === 'RESTRICTED' || employee?.onUnpaidLeave
                        ? 'Accrual paused'
                        : 'Accrual active'}
                    </Badge>
                  </Text>
                  <Flex ml="auto" gap="15px" align="center">
                    <Text textStyle="smallMedium" color="brand.purple">
                      {employee?.onUnpaidLeave ? 'On' : 'Off'}
                    </Text>
                    <Switch
                      colorScheme="badge.invited"
                      ml="auto"
                      size="lg"
                      isChecked={employee?.onUnpaidLeave}
                      isDisabled={
                        employee?.status &&
                        !['CREATED', 'INVITED', 'ACTIVE', 'LEAVING'].includes(employee?.status)
                      }
                      onChange={onOpenManageUnpaidLeave}
                    />
                  </Flex>
                </Flex>
              </Flex>
              <Flex
                w="full"
                flexDir={{ base: 'column', md: 'row' }}
                justifyContent="start"
                alignItems={{ base: 'start', md: 'center' }}
                p="20px"
                borderTop="1px solid"
                borderColor="brand.lightGray1"
                gap="20px"
              >
                <Text as="h6" textStyle="h6SemiBold" color="brand.black" maxW="200px" w="full">
                  Leave date
                </Text>

                <Flex
                  w="full"
                  flexDir={{ base: 'column', md: 'row' }}
                  justifyContent="start"
                  alignItems={{ base: 'start', md: 'center' }}
                  rowGap="20px"
                  columnGap="10px"
                >
                  <Text color="brand.black" textStyle="smallRegular">
                    {employee?.terminationDate &&
                    ['LEAVING', 'LEFT', 'RESTRICTED'].includes(employee.status) ? (
                      <Badge
                        colorScheme={
                          employee.status === 'LEAVING' || employee.status === 'RESTRICTED'
                            ? 'badge.leaving'
                            : 'badge.left'
                        }
                      >
                        {employee.status === 'LEAVING' || employee.status === 'RESTRICTED'
                          ? `Leaving on ${format(
                              parseISO(employee?.terminationDate),
                              DEFAULT_DATE_FORMAT
                            )}`
                          : `Left on ${format(
                              parseISO(employee?.terminationDate),
                              DEFAULT_DATE_FORMAT
                            )}`}
                      </Badge>
                    ) : (
                      '—'
                    )}
                  </Text>

                  <Button
                    variant="link"
                    size="sm"
                    leftIcon={<CalendarOutline boxSize="20px" />}
                    ml={{ base: 0, md: 'auto' }}
                    onClick={
                      employee?.terminationDate ? onOpenEditTerminationDate : onOpenTerminate
                    }
                    isDisabled={employee?.status === 'LEFT'}
                  >
                    {employee?.terminationDate ? 'Manage leave date' : 'Set leave date'}
                  </Button>
                </Flex>
              </Flex>

              <PreviousEmploymentSection previousIndividualId={employee?.previousIndividualId} />
            </Flex>
            <Flex
              flexDir="column"
              borderRadius="10px"
              border="1px solid"
              borderColor="brand.lightGray1"
              mx={{ base: 0, md: '60px', xl: '100px' }}
              flexWrap="wrap"
            >
              <Flex w="full" justifyContent="start" p="20px">
                <Text color="brand.purple" textStyle="h5Bold">
                  Aslan details
                </Text>
              </Flex>

              {shouldShowAllowanceSection && (
                <>
                  {/* Global limit section - show if there's global allowance data */}
                  {(payCalendar?.globalAllowancePercentage !== undefined ||
                    payCalendar?.odpAllowancePercentage !== undefined ||
                    employee?.globalAllowancePercentage !== undefined) && (
                    <Flex
                      w="full"
                      flexDir={{ base: 'column', md: 'row' }}
                      justifyContent="start"
                      alignItems={{ base: 'start', md: 'center' }}
                      p="20px"
                      borderTop="1px solid"
                      borderColor="brand.lightGray1"
                      gap="20px"
                    >
                      <Text
                        as="h6"
                        textStyle="h6SemiBold"
                        color="brand.black"
                        maxW="200px"
                        w="full"
                      >
                        Global limit (%)
                      </Text>

                      <Flex w="full" alignItems="center">
                        <Text color="brand.black" textStyle="smallRegular">
                          {employee?.globalAllowancePercentage !== undefined
                            ? `${employee?.globalAllowancePercentage}% (individual setting)`
                            : `${
                                payCalendar?.globalAllowancePercentage ||
                                payCalendar?.odpAllowancePercentage
                              }% (pay calendar default)`}
                        </Text>

                        <Button
                          variant="link"
                          size="sm"
                          ml="auto"
                          onClick={onOpenEditSalaryAllowance}
                          isDisabled={employee?.status === 'LEFT'}
                        >
                          Edit % allowance
                        </Button>
                      </Flex>
                    </Flex>
                  )}

                  {/* On-demand pay sub-limit section - show if there's ODP allowance data */}
                  {payCalendar?.odpAllowancePercentage !== undefined && (
                    <Flex
                      w="full"
                      flexDir={{ base: 'column', md: 'row' }}
                      justifyContent="start"
                      alignItems={{ base: 'start', md: 'center' }}
                      p="20px"
                      borderTop="1px solid"
                      borderColor="brand.lightGray1"
                      gap="20px"
                    >
                      <Text
                        as="h6"
                        textStyle="h6SemiBold"
                        color="brand.black"
                        maxW="200px"
                        w="full"
                      >
                        On-demand pay sub-limit (%)
                      </Text>

                      <Flex w="full" alignItems="center">
                        <Text color="brand.black" textStyle="smallRegular">
                          {employee?.odpAllowancePercentage !== undefined
                            ? `${employee?.odpAllowancePercentage}% (individual setting)`
                            : `${payCalendar?.odpAllowancePercentage}% (pay calendar default)`}
                        </Text>

                        <Button
                          variant="link"
                          size="sm"
                          ml="auto"
                          onClick={onOpenEditSalaryAllowance}
                          isDisabled={employee?.status === 'LEFT'}
                        >
                          Edit % allowance
                        </Button>
                      </Flex>
                    </Flex>
                  )}
                </>
              )}
            </Flex>
          </Flex>
        </Flex>
      )}
    </AuthenticatedPageWrapper>
  );
}

export default DefaultEmployeeProfile;
